/* ==========================================================================
   Lucky Goat Coffee — accessibility remediation, markup/behaviour fixes
   Theme: Symmetry ("Lucky Goat Website 2.0.1")
   Prepared: 2026-09-03

   WHAT THIS IS — AND IS NOT
   -------------------------
   This is NOT an accessibility overlay/widget. It does not add a toolbar, does
   not detect disability, and does not alter presentation for some users only.
   It is first-party JavaScript that applies a fixed, auditable list of
   corrections to this theme's own markup — the same corrections listed in
   liquid-patches.md, in the layer that can ship today.

   Editing the Liquid templates (liquid-patches.md) is the PREFERRED fix and
   should still be done: it survives with-JS-disabled, is visible to code
   review, and does not depend on execution order. Treat this file as the
   bridge that closes Level A gaps while those template edits are scheduled.

   HOW TO APPLY
   ------------
     1. Shopify admin > Online Store > Themes > ... > Edit code
     2. Assets > Add a new asset > create  assets/a11y-fixes.js
     3. Paste this file in
     4. In layout/theme.liquid, immediately before </body>, add:
          <script src="{{ 'a11y-fixes.js' | asset_url }}" defer></script>

   Every fix below corresponds to a defect measured on the live site with a
   headless-Chrome accessibility harness on 2026-09-03.
   ========================================================================== */

(function () {
  'use strict';

  var SR_ONLY = 'a11y-sr-only'; // defined in lucky-goat-a11y-fixes.css

  /* ----------------------------------------------------------------------
     FIX A — Announce links that open a new window.
              WCAG 3.2.2 (A), 2.4.4 (A)   [complaint items 4 and 13]

     Verified defects:
       <a href="https://www.facebook.com/LuckyGoatCoffee"  target="_blank" rel="noopener">Facebook</a>
       <a href="https://www.instagram.com/luckygoatcoffee" target="_blank" rel="noopener">Instagram</a>
       <a href="https://www.pinterest.com/luckygoatcoffee/" target="_blank" rel="noopener">Pinterest</a>
       <a href="https://luckygoatcoffee.com/policies/subscription-policy" target="_blank">View Subscription Policy</a>

     None warned the user. The subscription-policy link also lacked
     rel="noopener", which is a security issue (reverse tabnabbing) as well.

     NOTE for the record: complaint item 3 alleges the "View Product" links
     open a new window. They do not — all 10 were measured with no target
     attribute at all. No fix is needed there for that reason.
     ---------------------------------------------------------------------- */
  function announceNewWindow() {
    var links = document.querySelectorAll('a[target="_blank"]');
    Array.prototype.forEach.call(links, function (a) {
      if (a.getAttribute('data-a11y-newwin') === '1') return;

      // Close the reverse-tabnabbing hole while we are here.
      var rel = (a.getAttribute('rel') || '').toLowerCase();
      if (rel.indexOf('noopener') === -1) {
        a.setAttribute('rel', (rel ? rel + ' ' : '') + 'noopener noreferrer');
      }

      // Skip if some form of warning is already present.
      var existing = (
        a.getAttribute('aria-label') + ' ' + a.textContent + ' ' + (a.getAttribute('title') || '')
      ).toLowerCase();
      if (/new window|new tab|opens in/.test(existing)) {
        a.setAttribute('data-a11y-newwin', '1');
        return;
      }

      // Prefer appending real (screen-reader-only) text over overwriting
      // aria-label: an aria-label would clobber the visible name and break
      // voice-control users who say "click Facebook".
      var note = document.createElement('span');
      note.className = SR_ONLY;
      note.textContent = ' (opens in a new window)';
      a.appendChild(note);
      a.setAttribute('data-a11y-newwin', '1');
    });
  }

  /* ----------------------------------------------------------------------
     FIX B — Remove empty headings from the accessibility tree.
              WCAG 1.3.1 (A), 2.4.6 (AA)   [complaint item 10]

     Verified on the product page: two <h4></h4> elements with no content.
     A screen reader announces "heading level 4" with no name, and they appear
     in the heading list as blank entries.

     role="presentation" strips the heading semantics while leaving the element
     in place for layout. The template fix is to not emit them at all — see
     liquid-patches.md item 3.
     ---------------------------------------------------------------------- */
  function dropEmptyHeadings() {
    var hs = document.querySelectorAll('h1,h2,h3,h4,h5,h6');
    Array.prototype.forEach.call(hs, function (h) {
      var hasText = h.textContent.replace(/\s+/g, '').length > 0;
      var hasImgName = !!h.querySelector('img[alt]:not([alt=""]), svg[role="img"][aria-label]');
      // Only a genuinely childless, textless heading such as the verified
      // `<h4></h4>`. Requiring zero child elements avoids stripping a heading
      // whose text is injected later by a section or third-party app.
      var trulyBare = h.children.length === 0;
      if (!hasText && !hasImgName && trulyBare) {
        h.setAttribute('role', 'presentation');
        h.setAttribute('data-a11y-emptied', '1');
      }
    });
  }

  /* ----------------------------------------------------------------------
     FIX C — Correct the announced heading levels.
              WCAG 1.3.1 (A), 2.4.6 (AA)   [complaint items 9 and 11]

     Verified outline on the product page (reading order):
        h2 Wholesale & Bulk Coffee   <- announcement bar, before the h1
        h2 Wholesale & Bulk Coffee   <- duplicated (mobile + desktop copy)
        h1 Billy Goat Breakfast Blend
        h4 (empty)  x2
        h4 You may also like         <- jumps h1 -> h4
        h2 Reviews
        h2 Never miss out
        h6 ABOUT / CAFES / RESOURCES <- jumps h2 -> h6   [item 11 confirmed]

     aria-level overrides the announced level without changing the visual
     styling the tag provides (ARIA technique ARIA12). The footer titles become
     level 2 sections, and "You may also like" becomes level 2.
     ---------------------------------------------------------------------- */
  function fixHeadingLevels() {
    // Footer column titles: h6 -> announced as level 2.
    var footerTitles = document.querySelectorAll('h6.section-footer__title');
    Array.prototype.forEach.call(footerTitles, function (h) {
      h.setAttribute('aria-level', '2');
    });

    // Related-products heading: h4 -> level 2.
    var hs = document.querySelectorAll('h4');
    Array.prototype.forEach.call(hs, function (h) {
      if (/you may also like|related|recommend/i.test(h.textContent)) {
        h.setAttribute('aria-level', '2');
      }
    });

    // The announcement bar sits before the h1 and is promotional, not
    // structural. Demote it so it stops preceding the page's real h1.
    var ann = document.querySelectorAll('.announcement-bar h2, .announcement-bar h3');
    Array.prototype.forEach.call(ann, function (h) {
      h.setAttribute('aria-level', '2');
    });
  }

  /* ----------------------------------------------------------------------
     FIX D — Controls marked up as links that do not navigate.
              WCAG 4.1.2 (A)

     Verified defects (href="#", so they navigate nowhere):
        <a href="#" class="navigation__link" aria-haspopup aria-expanded>Resources</a>
        <a href="#" class="navigation__link" aria-haspopup aria-expanded>Goods</a>
        <a href="#" class="navigation__children-toggle">…svg…</a>       (x3)
        <a href="#" data-quantity="down">Minus</a>
        <a href="#" data-quantity="up">Plus</a>

     A screen reader announces "link", so the user expects to be taken
     somewhere; instead a menu expands or a quantity changes.

     role="button" corrects the announced role. Because a native <a> fires its
     activation on Enter but NOT on Space, we add Space handling so the control
     behaves the way its announced role promises.
     ---------------------------------------------------------------------- */
  function fixAnchorButtons() {
    var sel = [
      'a[href="#"].navigation__link',
      'a[href="#"].navigation__children-toggle',
      'a[href="#"][data-quantity]',
      'a[href="#"].fera-productReviews-writeReviewLink'
    ].join(',');

    var anchors = document.querySelectorAll(sel);
    Array.prototype.forEach.call(anchors, function (a) {
      if (a.getAttribute('data-a11y-button') === '1') return;
      a.setAttribute('role', 'button');

      // Icon-only toggles need a name; the quantity steppers have svg text.
      if (!a.getAttribute('aria-label') && !a.textContent.replace(/\s+/g, '')) {
        if (a.classList.contains('navigation__children-toggle')) {
          a.setAttribute('aria-label', 'Toggle submenu');
        }
      }
      if (a.hasAttribute('data-quantity')) {
        a.setAttribute(
          'aria-label',
          a.getAttribute('data-quantity') === 'up' ? 'Increase quantity' : 'Decrease quantity'
        );
      }

      a.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          a.click();
        }
      });
      a.setAttribute('data-a11y-button', '1');
    });
  }

  /* ----------------------------------------------------------------------
     FIX E — The page-shade overlay is a focusable, invisible, unlabelled
             anchor spanning the viewport.        WCAG 2.4.7 (AA), 4.1.2 (A)

     Verified markup:
        <a href="#" class="page-shade" aria-label="Toggle menu"></a>
     and on the homepage the label had not been translated at all:
        aria-label="general.navigation_menu.toggle_aria_label"

     Measured at Tab stop 45: 1280x0 px, opacity 0, still in the tab order.
     A keyboard user lands on nothing, with a raw translation key announced.

     It is a click-catcher for closing the mobile menu, so it should not be a
     tab stop while the menu is closed.
     ---------------------------------------------------------------------- */
  function fixPageShade() {
    // Verified class names on this theme: the mobile menu click-catcher ships
    // as `.page-shade` on product pages and `.header-shade.mobile-nav-toggle`
    // in the header. Both are <a href="#"> with no content.
    var shades = document.querySelectorAll(
      'a.page-shade, .page-shade, a.header-shade, .mobile-nav-toggle, a.nav-shade'
    );
    Array.prototype.forEach.call(shades, function (el) {
      var label = el.getAttribute('aria-label') || '';
      // Never announce a raw i18n key such as "general.navigation_menu.x".
      if (/^[a-z0-9_]+(\.[a-z0-9_]+){2,}$/i.test(label.trim()) || !label.trim()) {
        // The live site ships aria-label="general.navigation_menu.toggle_aria_label"
        // -- an untranslated locale key, announced verbatim by a screen reader.
        el.setAttribute('aria-label', 'Toggle navigation menu');
      }
      // Take it out of the tab order and the AT tree while it is not active.
      var active = el.classList.contains('is-visible') || el.classList.contains('active');
      if (!active) {
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
      } else {
        el.removeAttribute('aria-hidden');
        el.removeAttribute('tabindex');
      }
    });
  }

  /* ----------------------------------------------------------------------
     FIX F — Announce Add to Cart and Show More results.
              WCAG 4.1.3 (AA)   [complaint items 5 and 12 — both CONFIRMED]

     Measured: clicking "Add to Cart" changed the visible cart text but no
     aria-live region, role="status", role="alert" or <output> changed within
     6 seconds. Same for the reviews "Show More" button. A screen reader user
     gets no confirmation and may activate the control repeatedly.

     We add one polite status region and announce into it when the cart
     summary changes, so the announcement is driven by the actual result
     rather than by the click.
     ---------------------------------------------------------------------- */
  function installStatusRegion() {
    if (document.getElementById('a11y-status')) return document.getElementById('a11y-status');
    var live = document.createElement('div');
    live.id = 'a11y-status';
    live.className = SR_ONLY;
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    document.body.appendChild(live);
    return live;
  }

  function say(message) {
    var live = installStatusRegion();
    // Clear first so repeating the same message is still announced.
    live.textContent = '';
    window.setTimeout(function () {
      live.textContent = message;
    }, 60);
  }

  function watchCart() {
    // On this theme the cart count element does not exist while the cart is
    // empty -- `.cart-link` renders only the word "Cart" plus an icon. So we
    // must observe the cart LINK (always present) and react to any change
    // inside it, rather than looking up a count node once at page load.
    var link = document.querySelector('.cart-link, a[href$="/cart"], [class*="header-cart"]');
    if (!link) return;

    var read = function () {
      return link.textContent.replace(/\s+/g, ' ').trim();
    };
    var last = read();

    var mo = new MutationObserver(function () {
      var now = read();
      if (now === last) return;
      last = now;
      var n = (now.match(/\d+/) || [])[0];
      say(
        n
          ? 'Added to cart. Cart now has ' + n + ' item' + (n === '1' ? '' : 's') + '.'
          : 'Cart updated.'
      );
    });
    mo.observe(link, { childList: true, characterData: true, subtree: true, attributes: true });

    // The cart drawer is a separate subtree that appears on add; announce it
    // too, since some themes update the drawer without touching the header.
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target && e.target.closest && e.target.closest('button, [role="button"]');
        if (!btn) return;
        var name = (btn.getAttribute('aria-label') || btn.textContent || '').trim();
        if (!/add to cart|add to bag/i.test(name)) return;
        window.setTimeout(function () {
          if (read() === last) say('Added to cart.');
        }, 1200);
      },
      true
    );
  }

  function watchShowMore() {
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target && e.target.closest && e.target.closest('button, [role="button"], a');
        if (!btn) return;
        var name = (btn.getAttribute('aria-label') || btn.textContent || '').trim();
        if (!/^show more$|^load more$|^read more$/i.test(name)) return;

        // Announce the outcome after the widget has expanded, not the click.
        window.setTimeout(function () {
          say('Additional content loaded.');
        }, 700);
      },
      true
    );
  }

  /* ----------------------------------------------------------------------
     FIX G — Give the skip-link target a focus anchor.   WCAG 2.4.1 (A)

     The skip link already works: it is Tab stop 1, targets
     <main id="content" role="main">, and activating it does move past the
     header — which is why complaint item 1 does not reproduce.

     However, because the target is not focusable, Chrome only moves the
     "sequential focus navigation starting point"; DOM focus stays on <body>.
     Most browsers and screen readers follow correctly, but adding
     tabindex="-1" makes focus land on the target itself in every engine.
     ---------------------------------------------------------------------- */
  function anchorSkipTarget() {
    var main = document.querySelector('main#content, main[role="main"], main');
    if (main && !main.hasAttribute('tabindex')) {
      main.setAttribute('tabindex', '-1');
      // Suppress the focus ring on a container that only receives focus
      // programmatically, while leaving real controls' rings intact.
      main.style.outline = 'none';
    }
  }

  /* ----------------------------------------------------------------------
     FIX H — Name the unlabelled promotional image link.
              WCAG 4.1.2 (A), 2.4.4 (A)

     Verified: <a class="image-overlay__image-link" href="https://tpgo.ca/xrSfRX">
     wraps only a background-image div, so it has no accessible name and is
     announced as bare "link".

     We derive a name from the overlay's own visible heading/text where one
     exists, rather than inventing copy. Where nothing is derivable it is left
     for an editor — see liquid-patches.md item 7.
     ---------------------------------------------------------------------- */
  function nameImageLinks() {
    var links = document.querySelectorAll('a.image-overlay__image-link, a[class*="image-overlay"]');
    Array.prototype.forEach.call(links, function (a) {
      if ((a.textContent || '').replace(/\s+/g, '') || a.getAttribute('aria-label')) return;

      var scope = a.closest('.image-overlay, section, .shopify-section') || a.parentElement;
      var src = scope && scope.querySelector('h1,h2,h3,h4,.image-overlay__heading,.btn');
      var name = src ? src.textContent.replace(/\s+/g, ' ').trim() : '';
      if (name) a.setAttribute('aria-label', name.slice(0, 100));
    });
  }

  /* ------------------------------- run -------------------------------- */
  function run() {
    try { anchorSkipTarget(); } catch (e) {}
    try { announceNewWindow(); } catch (e) {}
    try { dropEmptyHeadings(); } catch (e) {}
    try { fixHeadingLevels(); } catch (e) {}
    try { fixAnchorButtons(); } catch (e) {}
    try { fixPageShade(); } catch (e) {}
    try { nameImageLinks(); } catch (e) {}
    try { watchCart(); } catch (e) {}
    try { watchShowMore(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  // Third-party apps (reviews, upsells) and the cart drawer inject markup
  // after load, so re-apply the idempotent DOM fixes when the tree changes.
  var pending = null;
  var observer = new MutationObserver(function () {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      try { announceNewWindow(); } catch (e) {}
      try { dropEmptyHeadings(); } catch (e) {}
      try { fixHeadingLevels(); } catch (e) {}
      try { fixAnchorButtons(); } catch (e) {}
      try { fixPageShade(); } catch (e) {}
    }, 400);
  });

  function startObserving() {
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.body) startObserving();
  else document.addEventListener('DOMContentLoaded', startObserving, { once: true });
})();


/* ==========================================================================
   FIX I — The search control announces as a link but behaves as a disclosure.
                                                            WCAG 4.1.2 (A)
   Complaint item 8.

   <a class="show-search-link" href="/search" aria-label="Search">

   JavaScript intercepts the click and opens <div id="main-search"> in place,
   so a screen reader announces "link" while the control actually toggles a
   panel. The href is a real, working page, which is why liquid-patches.md §8d
   calls this arguable: without JS the link navigates and search still works.
   This closes it beyond argument while KEEPING that no-JS fallback.

   Three things are required, not one:
     1. role="button"        so the announced role matches the behaviour
     2. aria-expanded        kept in sync with the panel, or the role lies
     3. Space key handling   an <a role="button"> must activate on Space;
                             anchors natively respond only to Enter, and a
                             keyboard user pressing Space would scroll instead

   Panel state is read from computed style rather than a theme class name, so a
   Symmetry update that renames its classes cannot silently desynchronise this.
   ========================================================================== */
(function () {
  'use strict';

  var links = document.querySelectorAll('a.show-search-link');
  if (!links.length) return;
  var panel = document.getElementById('main-search');

  function isOpen() {
    if (!panel) return false;
    if (panel.offsetParent === null) return false;
    var cs = window.getComputedStyle(panel);
    return cs.display !== 'none' &&
           cs.visibility !== 'hidden' &&
           parseFloat(cs.opacity || '1') > 0.01;
  }

  function sync() {
    var open = isOpen() ? 'true' : 'false';
    for (var i = 0; i < links.length; i++) {
      if (links[i].getAttribute('aria-expanded') !== open) {
        links[i].setAttribute('aria-expanded', open);
      }
    }
  }

  for (var i = 0; i < links.length; i++) {
    var a = links[i];
    a.setAttribute('role', 'button');
    a.setAttribute('aria-expanded', 'false');
    if (panel && panel.id) a.setAttribute('aria-controls', panel.id);

    a.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Spacebar' || e.keyCode === 32) {
        e.preventDefault();
        this.click();
      }
    });
    a.addEventListener('click', function () { window.setTimeout(sync, 60); });
  }

  if (panel && window.MutationObserver) {
    new MutationObserver(sync).observe(panel, {
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'hidden']
    });
  }

  // The close button and Escape both dismiss the panel without touching the link.
  var closer = document.querySelector('.main-search__close');
  if (closer) closer.addEventListener('click', function () { window.setTimeout(sync, 60); });
  document.addEventListener('keyup', function (e) {
    if (e.key === 'Escape' || e.keyCode === 27) window.setTimeout(sync, 60);
  });

  sync();
})();
